/**
 * Call back once a document is ready, however late it arrives.
 *
 * `repo.find` rejects the moment every source has said "not here", which on
 * a reload is routinely before the peer that has it connects. A watcher that
 * awaited it once and swallowed the rejection never heard of the document
 * again (RFC 0339 review). This follows the query instead, the way
 * `useDocument` does: `unavailable` is a state a query leaves when a peer
 * arrives, so it is waited through. Returns the unsubscribe.
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { holdDocument } from '../world/repo/held-docs'

interface Progress {
  peek(): { state: string; handle?: DocHandle<unknown> }
  subscribe(cb: () => void): () => void
}

export function followDocument(
  repo: Repo,
  id: string,
  onReady: (handle: DocHandle<unknown>) => void,
): () => void {
  let done = false
  let progress: Progress
  try {
    progress = repo.findWithProgress<unknown>(
      id as AnyDocumentId,
    ) as unknown as Progress
  } catch {
    // Not a document id at all: there is nothing to wait for.
    return () => {}
  }
  const check = () => {
    if (done) return
    const now = progress.peek()
    if (now.state === 'ready' && now.handle) {
      done = true
      onReady(now.handle)
    }
  }
  // The caller keeps the handle until it unsubscribes (RFC 0317 §3d).
  const release = holdDocument(repo, id)
  const off = progress.subscribe(check)
  check()
  return () => {
    done = true
    off()
    release()
  }
}
